/**
 * BathBreakdownEditor — Sell Hub bath-section control.
 *
 * Three NumericStepper rows (full / three-quarter / half) feed two derived
 * readouts:
 *   - "Bathrooms"  — the plain room count (full + three_quarter + half).
 *   - "Total baths" — the MLS weighted decimal
 *     (full * 1.0 + three_quarter * 0.75 + half * 0.5).
 *
 * Both are shown because they answer different questions: a seller with
 * 1 full + 1 three-quarter + 2 half baths has *4 bathrooms* but an MLS
 * decimal of *2.75*. Showing only the decimal reads as "you have 2.75
 * bathrooms", which sellers reported as wrong/confusing.
 *
 * Surfaces a "Estimated breakdown — please confirm" badge when the editor
 * was pre-filled from a legacy `bathrooms` float. The badge clears the
 * moment the seller touches any stepper.
 *
 * Hard-gates publish via the zero-bath confirm checkbox when the total
 * resolves to 0 — caller reads `confirmedZero` and folds it into
 * `canPublish()`.
 *
 * Props:
 *  - value: { full: number, threeQuarter: number, half: number }
 *  - wasEstimated: boolean — initial render flag; the legacy badge displays
 *      until the seller changes a value.
 *  - confirmedZero: boolean — whether the seller has explicitly confirmed
 *      a zero-bath listing.
 *  - onChange: (next: {full, threeQuarter, half}) => void
 *  - onConfirmedZeroChange: (next: boolean) => void
 */

import React, { useMemo, useState, useCallback } from 'react';
import NumericStepper from '../ui/NumericStepper';
import Badge from '../ui/Badge';
import './BathBreakdownEditor.css';

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Decompose a legacy `bathrooms` float into (full, three_quarter, half),
 * preferring to over-attribute to `full` in the ambiguous 0.25 case (see
 * design spec §2.3 and PRD §5.2 edge).
 *
 * Cases (rem = bathrooms - floor(bathrooms)):
 *  rem >= 0.75  → three_quarter = 1, half = 0
 *  rem >= 0.5   → three_quarter = 0, half = 1
 *  rem >= 0.25  → full += 1, three_quarter = 0, half = 0 (round up to full)
 *  else         → three_quarter = 0, half = 0
 */
export function decomposeLegacyBaths(bathrooms) {
  if (typeof bathrooms !== 'number' || Number.isNaN(bathrooms) || bathrooms < 0) {
    return { full: 0, threeQuarter: 0, half: 0 };
  }
  const full = Math.floor(bathrooms);
  const remainder = round2(bathrooms - full);
  if (remainder >= 0.75) return { full, threeQuarter: 1, half: 0 };
  if (remainder >= 0.5) return { full, threeQuarter: 0, half: 1 };
  if (remainder >= 0.25) return { full: full + 1, threeQuarter: 0, half: 0 };
  return { full, threeQuarter: 0, half: 0 };
}

export default function BathBreakdownEditor({
  value,
  wasEstimated = false,
  confirmedZero = false,
  onChange,
  onConfirmedZeroChange,
}) {
  // `internalEstimated` mirrors `wasEstimated` but clears on first touch so
  // we don't show the "Estimated breakdown" badge after the seller has
  // confirmed their counts.
  const [internalEstimated, setInternalEstimated] = useState(wasEstimated);

  // If the parent flips wasEstimated from false→true (e.g. switching listings
  // in edit mode), pick that up. The reverse — true→false — is owned by the
  // editor when the seller touches a stepper, so we don't override here.
  React.useEffect(() => {
    if (wasEstimated) setInternalEstimated(true);
  }, [wasEstimated]);

  const total = useMemo(() => {
    const f = value?.full || 0;
    const tq = value?.threeQuarter || 0;
    const h = value?.half || 0;
    return round2(f * 1.0 + tq * 0.75 + h * 0.5);
  }, [value]);

  // Plain room count — what a seller means when they say "4 bathrooms".
  const roomCount = useMemo(() => {
    const f = value?.full || 0;
    const tq = value?.threeQuarter || 0;
    const h = value?.half || 0;
    return f + tq + h;
  }, [value]);

  const totalDisplay = total.toFixed(2);

  const handleChange = useCallback(
    (key) => (next) => {
      if (internalEstimated) setInternalEstimated(false);
      onChange({
        full: key === 'full' ? next : value?.full || 0,
        threeQuarter: key === 'threeQuarter' ? next : value?.threeQuarter || 0,
        half: key === 'half' ? next : value?.half || 0,
      });
    },
    [internalEstimated, onChange, value],
  );

  const isZero = total === 0;

  return (
    <div className="bath-breakdown" data-testid="bath-breakdown">
      <div className="bath-breakdown__header">
        <h3 className="listing-form__section-title bath-breakdown__title">
          Bathrooms
        </h3>
        {internalEstimated && (
          <Badge
            variant="verification"
            value="unverified"
            data-testid="bath-estimated-badge"
          >
            Estimated breakdown — please confirm
          </Badge>
        )}
      </div>
      <p className="listing-form__hint">
        Pick the count for each type. Total updates automatically.
      </p>

      <div className="bath-breakdown__grid">
        <NumericStepper
          id="bath-full"
          testId="bath-full"
          label="Full baths"
          description="toilet · sink · tub or shower"
          value={value?.full || 0}
          min={0}
          max={20}
          onChange={handleChange('full')}
        />
        <NumericStepper
          id="bath-three-quarter"
          testId="bath-three-quarter"
          label="¾ baths"
          srLabel="Three-quarter baths"
          description="toilet · sink · shower"
          value={value?.threeQuarter || 0}
          min={0}
          max={20}
          onChange={handleChange('threeQuarter')}
        />
        <NumericStepper
          id="bath-half"
          testId="bath-half"
          label="Half baths"
          description="toilet · sink"
          value={value?.half || 0}
          min={0}
          max={20}
          onChange={handleChange('half')}
        />
      </div>

      <div
        className="bath-breakdown__total"
        aria-live="polite"
        data-testid="bath-total"
      >
        <div className="bath-breakdown__total-row">
          <span className="bath-breakdown__total-label">Bathrooms</span>
          <output className="bath-breakdown__total-value" data-testid="bath-room-count-value">
            {roomCount}
          </output>
        </div>
        <div className="bath-breakdown__total-row">
          <span className="bath-breakdown__total-label bath-breakdown__total-label--secondary">
            Total baths (MLS)
          </span>
          <output
            className="bath-breakdown__total-value bath-breakdown__total-value--secondary"
            data-testid="bath-total-value"
          >
            {totalDisplay}
          </output>
        </div>
        <p className="bath-breakdown__total-hint">
          Computed from the breakdown above. <strong>Bathrooms</strong> counts the
          rooms; <strong>Total baths</strong> is the MLS decimal (full 1 · &frac34; 0.75 ·
          half 0.5). We show the breakdown on listings, not these numbers.
        </p>
      </div>

      {isZero && (
        <label className="bath-breakdown__zero-check" data-testid="bath-zero-confirm">
          <input
            type="checkbox"
            checked={confirmedZero}
            onChange={(e) => onConfirmedZeroChange && onConfirmedZeroChange(e.target.checked)}
            data-testid="bath-zero-confirm-input"
          />
          <span>Confirm this property has no bathrooms.</span>
        </label>
      )}
    </div>
  );
}
